/**
 * Contact page data - Firebase-ready structure
 * Contains company contact information, addresses, and FAQs
 */
import { Timestamp } from 'firebase/firestore';

// Helper to create mock timestamps
const createTimestamp = (): Timestamp => {
  return {
    seconds: Math.floor(Date.now() / 1000),
    nanoseconds: 0,
    toDate: () => new Date(),
    toMillis: () => Date.now(),
    isEqual: () => false,
    valueOf: () => '',
  } as unknown as Timestamp;
};

// ==========================================
// TYPE DEFINITIONS
// ==========================================

export interface CompanyInfo {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  logo: string;
  foundedYear: number;
}

export interface Address {
  id: string;
  type: 'headquarters' | 'warehouse' | 'showroom' | 'branch';
  label: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  phone?: string;
  email?: string;
  mapUrl?: string;
  isPrimary: boolean;
}

export interface ContactMethod {
  id: string;
  type: 'phone' | 'email' | 'whatsapp' | 'toll-free';
  label: string;
  value: string;
  displayValue: string;
  icon: string;
  description: string;
  isPreferred: boolean;
}

export interface SocialLink {
  id: string;
  platform: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'youtube' | 'whatsapp';
  url: string;
  label: string;
  icon: string;
}

export interface BusinessHours {
  id: string;
  day: string;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

export interface SupportCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  email: string;
  responseTime: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
}

export interface ContactData {
  id: string;
  company: CompanyInfo;
  addresses: Address[];
  contactMethods: ContactMethod[];
  socialLinks: SocialLink[];
  businessHours: BusinessHours[];
  supportCategories: SupportCategory[];
  faqs: FAQ[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ==========================================
// MOCK DATA
// ==========================================

export const contactData: ContactData = {
  id: 'sunvivo-contact',
  company: {
    name: 'Sunvivo',
    legalName: 'Sunvivo Equipment Pvt. Ltd.',
    tagline: 'Premium Dental Equipment & Supplies',
    description: 'Your trusted partner for high-quality dental equipment, instruments, and consumables. Serving dental professionals across India since 2014.',
    logo: '/images/logo.png',
    foundedYear: 2014,
  },
  addresses: [
    {
      id: 'addr-hq',
      type: 'headquarters',
      label: 'Head Office',
      addressLine1: '123, Medical Equipment Complex',
      addressLine2: 'Sector 18, Industrial Area',
      city: 'Gurgaon',
      state: 'Haryana',
      pincode: '122015',
      country: 'India',
      phone: '+91 124 456 7890',
      email: 'info@sunvivo.in',
      mapUrl: 'https://maps.google.com/?q=Gurgaon+Haryana',
      isPrimary: true,
    },
    {
      id: 'addr-warehouse',
      type: 'warehouse',
      label: 'Warehouse & Distribution',
      addressLine1: '456, Logistics Hub',
      addressLine2: 'Near NH-8 Expressway',
      city: 'Manesar',
      state: 'Haryana',
      pincode: '122051',
      country: 'India',
      phone: '+91 124 789 0123',
      isPrimary: false,
    },
    {
      id: 'addr-showroom',
      type: 'showroom',
      label: 'Delhi Showroom',
      addressLine1: '789, Dental Equipment Plaza',
      addressLine2: 'Connaught Place',
      city: 'New Delhi',
      state: 'Delhi',
      pincode: '110001',
      country: 'India',
      phone: '+91 11 2345 6789',
      isPrimary: false,
    },
  ],
  contactMethods: [
    {
      id: 'cm-phone',
      type: 'phone',
      label: 'Call Us',
      value: '+919876543210',
      displayValue: '+91 98765 43210',
      icon: 'FiPhone',
      description: 'Mon-Sat, 9 AM - 6 PM',
      isPreferred: true,
    },
    {
      id: 'cm-whatsapp',
      type: 'whatsapp',
      label: 'WhatsApp',
      value: '+919876543210',
      displayValue: '+91 98765 43210',
      icon: 'FiMessageCircle',
      description: 'Quick responses, 24/7',
      isPreferred: true,
    },
    {
      id: 'cm-email',
      type: 'email',
      label: 'Email Us',
      value: 'support@sunvivo.in',
      displayValue: 'support@sunvivo.in',
      icon: 'FiMail',
      description: 'Response within 24 hours',
      isPreferred: false,
    },
  ],
  socialLinks: [
    {
      id: 'social-facebook',
      platform: 'facebook',
      url: 'https://facebook.com/sunvivo',
      label: 'Facebook',
      icon: 'FiFacebook',
    },
    {
      id: 'social-instagram',
      platform: 'instagram',
      url: 'https://instagram.com/sunvivo',
      label: 'Instagram',
      icon: 'FiInstagram',
    },
    {
      id: 'social-linkedin',
      platform: 'linkedin',
      url: 'https://linkedin.com/company/sunvivo',
      label: 'LinkedIn',
      icon: 'FiLinkedin',
    },
    {
      id: 'social-youtube',
      platform: 'youtube',
      url: 'https://youtube.com/@sunvivo',
      label: 'YouTube',
      icon: 'FiYoutube',
    },
    {
      id: 'social-whatsapp',
      platform: 'whatsapp',
      url: 'https://wa.me/919876543210',
      label: 'WhatsApp',
      icon: 'FiMessageCircle',
    },
  ],
  businessHours: [
    { id: 'bh-mon', day: 'Monday', openTime: '09:00', closeTime: '18:00', isClosed: false },
    { id: 'bh-tue', day: 'Tuesday', openTime: '09:00', closeTime: '18:00', isClosed: false },
    { id: 'bh-wed', day: 'Wednesday', openTime: '09:00', closeTime: '18:00', isClosed: false },
    { id: 'bh-thu', day: 'Thursday', openTime: '09:00', closeTime: '18:00', isClosed: false },
    { id: 'bh-fri', day: 'Friday', openTime: '09:00', closeTime: '18:00', isClosed: false },
    { id: 'bh-sat', day: 'Saturday', openTime: '10:00', closeTime: '16:00', isClosed: false },
    { id: 'bh-sun', day: 'Sunday', openTime: '', closeTime: '', isClosed: true },
  ],
  supportCategories: [
    {
      id: 'support-sales',
      name: 'Sales Inquiries',
      description: 'For product information, bulk orders, and pricing',
      icon: 'FiShoppingBag',
      email: 'sales@sunvivo.in',
      responseTime: 'Within 2 hours',
    },
    {
      id: 'support-orders',
      name: 'Order Support',
      description: 'Track orders, delivery updates, and shipping queries',
      icon: 'FiPackage',
      email: 'orders@sunvivo.in',
      responseTime: 'Within 4 hours',
    },
    {
      id: 'support-technical',
      name: 'Technical Support',
      description: 'Product usage, troubleshooting, and warranty claims',
      icon: 'FiTool',
      email: 'technical@sunvivo.in',
      responseTime: 'Within 24 hours',
    },
    {
      id: 'support-returns',
      name: 'Returns & Refunds',
      description: 'Return requests, refund status, and replacements',
      icon: 'FiRefreshCw',
      email: 'returns@sunvivo.in',
      responseTime: 'Within 24 hours',
    },
  ],
  faqs: [
    {
      id: 'faq-1',
      question: 'What are your delivery timeframes?',
      answer: 'We typically deliver within 3-5 business days for metro cities and 5-7 business days for other locations. Express delivery options are available for urgent orders at additional cost.',
      category: 'Shipping',
      order: 1,
    },
    {
      id: 'faq-2',
      question: 'Do you offer bulk discounts?',
      answer: 'Yes, we offer attractive discounts for bulk orders. Please contact our sales team at sales@sunvivo.in or call us for customized pricing based on your requirements.',
      category: 'Pricing',
      order: 2,
    },
    {
      id: 'faq-3',
      question: 'What is your return policy?',
      answer: 'We accept returns within 7 days of delivery for unused products in original packaging. Some items like sterile products and consumables are non-returnable for hygiene reasons.',
      category: 'Returns',
      order: 3,
    },
    {
      id: 'faq-4',
      question: 'Do you provide product warranty?',
      answer: 'All our equipment comes with manufacturer warranty. Handpieces typically have 6-12 months warranty, while larger equipment may have extended warranty options.',
      category: 'Warranty',
      order: 4,
    },
    {
      id: 'faq-5',
      question: 'Can I get a demo of equipment before purchasing?',
      answer: 'Yes, we offer product demonstrations for major equipment purchases. Visit our showroom in Delhi or contact us to schedule a demo at your clinic.',
      category: 'Sales',
      order: 5,
    },
    {
      id: 'faq-6',
      question: 'What payment methods do you accept?',
      answer: 'We accept UPI, credit/debit cards, net banking, and wallets via Razorpay, along with Cash on Delivery (COD). Bank transfers are preferred for bulk orders.',
      category: 'Payment',
      order: 6,
    },
  ],
  createdAt: createTimestamp(),
  updatedAt: createTimestamp(),
};

// Helper functions
export const getContactData = (): ContactData => contactData;

export const getPrimaryAddress = (): Address | undefined => {
  return contactData.addresses.find(addr => addr.isPrimary);
};

export const getPreferredContactMethods = (): ContactMethod[] => {
  return contactData.contactMethods.filter(cm => cm.isPreferred);
};

export const getFaqsByCategory = (category: string): FAQ[] => {
  return contactData.faqs.filter(faq => faq.category === category);
};

export const getAllFaqs = (): FAQ[] => {
  return [...contactData.faqs].sort((a, b) => a.order - b.order);
};
